import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

export const pendingImageJob = (job) => ["queued", "generating", "publishing"].includes(job.status);
const errorText = (error) => typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Could not complete the request. Check your connection and try again.";

export const useImageStudio = (user) => {
  const [models, setModels] = useState([]), [products, setProducts] = useState([]), [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState(null), [loading, setLoading] = useState(true);
  const [error, setError] = useState(""), [busy, setBusy] = useState("");
  const alive = useRef(false), request = useRef(null), actionLock = useRef(false);
  const updateJob = useCallback((job) => setJobs((old) => [job, ...old.filter((entry) => entry.id !== job.id)].sort((a, b) => b.created_at.localeCompare(a.created_at))), []);
  const load = useCallback(async (signal) => {
    const [m, j, p] = await Promise.all([
      axios.get(`${API}/image-studio/models`, { signal }), axios.get(`${API}/image-studio/jobs`, { signal }),
      user.is_admin ? axios.get(`${API}/admin/products`, { signal }) : Promise.resolve({ data: [] }),
    ]);
    if (!alive.current || signal?.aborted) return;
    setModels(m.data); setJobs(j.data); setProducts(p.data); setError("");
  }, [user.is_admin]);
  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    load(controller.signal).catch((err) => { if (!controller.signal.aborted) setError(errorText(err)); }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { alive.current = false; controller.abort(); };
  }, [load]);
  const hasPending = jobs.some(pendingImageJob);
  useEffect(() => {
    if (!hasPending) return;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/image-studio/jobs`, { signal: controller.signal });
        if (!controller.signal.aborted) { setJobs(data); setError(""); }
      } catch (err) { if (!controller.signal.aborted) setError(errorText(err)); }
      if (!controller.signal.aborted) timer = setTimeout(poll, 2500);
    };
    timer = setTimeout(poll, 2000);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [hasPending]);
  const perform = async (label, work) => {
    if (actionLock.current) return false;
    actionLock.current = true; setBusy(label); setError("");
    try { await work(); return true; }
    catch (err) { if (alive.current) setError(errorText(err)); return false; }
    finally { actionLock.current = false; if (alive.current) setBusy(""); }
  };
  const generate = (body) => perform("generate", async () => {
    const fingerprint = JSON.stringify(body);
    if (request.current?.fingerprint !== fingerprint) request.current = { fingerprint, id: crypto.randomUUID() };
    const { data } = await axios.post(`${API}/image-studio/jobs`, { ...body, request_id: request.current.id });
    if (alive.current) { updateJob(data); setSelectedId(data.id); request.current = null; }
  });
  const publish = (id) => perform("publish", async () => {
    await axios.post(`${API}/image-studio/jobs/${id}/publish`);
    const { data } = await axios.get(`${API}/image-studio/jobs/${id}`);
    if (alive.current) updateJob(data);
  });
  const submit = (id, body) => perform("submit", async () => {
    await axios.post(`${API}/image-studio/jobs/${id}/enquiry`, body);
    const { data } = await axios.get(`${API}/image-studio/jobs/${id}`);
    if (alive.current) updateJob(data);
  });
  const refresh = () => perform("refresh", () => load());
  return { models, products, jobs, selectedId, setSelectedId, loading, error, busy, hasPending, generate, publish, submit, refresh };
};